/**
 * HTTP surface for voice and video calls — protocol/calls-v1.md.
 */

import { api } from '@/api/client'
import type { Call, CallMedia, UUID } from '@/api/types'

export interface IceServersResponse {
  ice_servers: RTCIceServer[]
  expires_at: string
}

export async function fetchIceServers(): Promise<IceServersResponse> {
  return api.get<IceServersResponse>('/calls/ice-servers')
}

export async function fetchActiveCall(roomId: UUID): Promise<Call | null> {
  const body = await api.get<{ call: Call | null }>(`/rooms/${roomId}/calls/active`)
  return body.call
}

export async function startCall(roomId: UUID, media: CallMedia): Promise<Call> {
  const body = await api.post<{ call: Call }>(`/rooms/${roomId}/calls`, { media })
  return body.call
}

/**
 * Joining says what this client can render. A client that cannot draw a second
 * video track must never be sent one — it keys remote media by user, so a
 * screen would replace the camera it is already showing. See
 * protocol/calls-v2-screen-sharing.md.
 */
export const CLIENT_CALL_FEATURES = ['screen_share'] as const

export async function joinCall(callId: UUID, features: readonly string[] = CLIENT_CALL_FEATURES): Promise<Call> {
  const body = await api.post<{ call: Call }>(`/calls/${callId}/join`, { features })
  return body.call
}

export async function declineCall(callId: UUID): Promise<void> {
  await api.post(`/calls/${callId}/decline`)
}

export async function leaveCall(callId: UUID): Promise<Call | null> {
  const body = await api.post<{ call: Call | null }>(`/calls/${callId}/leave`)
  return body.call
}
